import { observable, computed, action } from 'mobx';
import get from 'lodash/get';
import GdsModel from '~/glm/models/gds/gds-model';
import Collection from '~/glm/models/collection';
import CustomQuestionModel from '~/glm/models/gds/gds-custom-question-model';
import { formatToMilitary } from '~/glm/utils/format-time';
import { getHouseholdWithInvitations } from '~/glm/components/guest-list/utils/placeholderHousehold';
import { QUESTION_TYPE_MEAL } from '~/glm/utils/constants';

export default class GdsEventModel extends GdsModel {
  @observable id;
  @observable name;
  @observable customQuestionCollection = new Collection();
  @observable requireRsvp;
  @observable date;
  @observable time;
  @observable endTime;
  @observable fullAddress;
  @observable locationName;
  @observable subEvents = [];
  @observable attire;
  @observable notes;
  @observable visible;
  @observable rsvpErrors = {
    noGuest: false,
    noDate: false,
  };
  placeholderHousehold;
  type;
  mealsAttributes = [];
  calculatedMealCount = {};
  weddingId;

  constructor(event = {}) {
    super();
    this.id = event.id;
    this.name = event.name;
    this.type = event.type || 'WeddingEvent';
    this.date = event.date;
    this.time = event.time;
    this.endTime = event.endTime;
    this.requireRsvp = event.requireRsvp;
    this.location = event.location;
    this.attire = event.attire;
    this.notes = event.notes;
    this.visible = event.visible;
    if (event.location) {
      this.locationName = event.location.name;
      this.fullAddress = event.location.fullAddress;
    } else if (event.locationName || event.fullAddress) {
      this.fullAddress = event.fullAddress;
      this.locationName = event.locationName;
    }
    if (event.questions) {
      this.setCustomQuestions(event.questions);
    }

    if (event.subEvents) {
      this.subEvents = event.subEvents;
    }
    this.weddingId = event.weddingId;
  }

  @computed
  get apiEndpoint() {
    return `/v1/events${this.id ? `/${this.id}` : ''}`;
  }

  @computed
  get apiParams() {
    let params = {
      name: this.name,
      type: this.type,
      date: this.date,
      time: formatToMilitary(this.time) || undefined,
      requireRsvp: this.requireRsvp,
      location: { fullAddress: this.fullAddress, name: this.locationName },
      weddingId: this.weddingId,
    };

    if (this.questionCollection.size) {
      const questions = this.getCustomQuestions();
      params = { ...params, questions };
    }
    return params;
  }

  @computed
  get rsvp() {
    return this.requireRsvp;
  }

  @computed
  get isWeddingEvent() {
    return this.type === 'WeddingDay' || this.type === 'Ceremony';
  }

  @computed
  get guestCount() {
    return this.invitations.length;
  }

  @computed
  get invitedHouseholds() {
    return this.householdCollection
      ? this.householdCollection.filter(household => {
          return household.invitedGuests(this.id).length > 0;
        })
      : [];
  }

  getFilteredAndInvitedHouseholds = addressFilter => {
    if (addressFilter) {
      return this.invitedHouseholds.filter(household => !household.street1 && !household.city && !household.state);
    }

    return this.invitedHouseholds;
  };

  @computed
  get mealQuestion() {
    return this.customQuestionCollection.find(question => question.type === QUESTION_TYPE_MEAL);
  }

  @computed
  get invitations() {
    const invitedGuestInvitations = [];
    this.invitedHouseholds.forEach(household => {
      const invitations = household.guestInvitations(this.id);
      invitations.forEach(invitation => invitedGuestInvitations.push(invitation));
    });
    return invitedGuestInvitations;
  }

  @computed
  get acceptedCount() {
    return this.invitations.filter(invitation => invitation.rsvp === true).length;
  }

  @computed
  get declinedCount() {
    return this.invitations.filter(invitation => invitation.rsvp === false).length;
  }

  @computed
  get noResponseCount() {
    return this.invitations.filter(invitation => invitation.rsvp === null).length;
  }

  @action
  setRsvpError(errorType, bool) {
    this.rsvpErrors[errorType] = bool;
  }

  @computed
  get showRsvpErrors() {
    return this.rsvpErrors;
  }

  @action
  resetRsvpErrors() {
    Object.keys(this.rsvpErrors).forEach(error => {
      this.setRsvpError(error, false);
    });
  }

  @computed
  get getActiveRsvpErrors() {
    return Object.keys(this.showRsvpErrors).filter(errorType => this.showRsvpErrors[errorType]);
  }

  getPlaceholderHousehold() {
    if (!this.placeholderHousehold) {
      this.placeholderHousehold = getHouseholdWithInvitations(this.id, this.weddingId);
    }

    return this.placeholderHousehold;
  }

  rsvpErrorMessages(error) {
    const eventType = this.isWeddingEvent ? 'wedding' : 'event';
    const errors = {
      noGuest: "Oops! You can't turn on RSVP until you tell us who's invited. Add guests to this event first.",
      noDate: `Oops! You can't turn on RSVP until you tell us your ${eventType} date.`,
    };
    return errors[error];
  }

  @computed
  get getEventRsvpStatus() {
    return this.requireRsvp;
  }

  @computed
  get invitationList() {
    return this.invitationCollection.filter(invitation => invitation.eventId === this.id);
  }

  @action
  setCustomQuestions(questions) {
    this.customQuestionCollection.clear();
    if (questions) {
      questions.forEach(question => this.customQuestionCollection.add(new CustomQuestionModel(question)));
    }
  }

  getCustomQuestions() {
    if (!this.customQuestionCollection.size) {
      return [];
    }

    return this.customQuestionCollection.map(mobXQuestion => {
      // eslint-disable-next-line no-unused-vars
      const { dirty, options: mobXOptions, ...values } = mobXQuestion;
      const question = { ...values };
      if (mobXOptions) {
        question.options = mobXOptions.map(({ id, text, description }) => ({ id, text, description }));
      }

      return question;
    });
  }

  @computed
  get questions() {
    return this.getCustomQuestions();
  }

  hasMeals() {
    return !!get(this.mealQuestion, ['options', 'length']);
  }

  @action
  setLocation(location) {
    this.locationName = location.name;
    this.fullAddress = location.fullAddress;
  }

  mealCount(meal) {
    return this.invitations.filter(
      invitation => invitation.meal && invitation.rsvp && invitation.meal.toLowerCase() === meal.toLowerCase()
    ).length;
  }

  patch(params) {
    return super.patch(params).then(response => {
      this.setLocation(response.location);
      this.setCustomQuestions(response.questions);
      return response;
    });
  }
}
